import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toApiError } from "../api/classifyFailure";
import type {
  ApplyResult,
  Command,
  ProjectTree,
  SavedFilter,
  SeriesScope,
  Sort,
  TaskId,
  TaskPatch,
  TaskView,
} from "../api/generated";
import type { SyncStatus, TaskApi } from "../api/taskApi";
import { editCommand } from "../domain/commands";
import { parseQuickAdd } from "../domain/quickAdd";
import { QuickAddError, commandForQuickAdd } from "../domain/quickAddCommand";
import { skippedMessages } from "../domain/skipNotes";
import { tagCounts, inboxCount, type TagCount } from "../domain/tags";
import { allProjectPaths } from "../domain/projects";
import {
  BUILTIN_SHORTCUTS,
  DEFAULT_QUERY,
  builtinShortcut,
  currentShortcuts,
  groupingForSort,
  projectShortcut,
  savedFilterShortcut,
  tagShortcut,
  type Grouping,
  type ListQuery,
  type Shortcut,
} from "../domain/views";
import { useToasts, type Toasts } from "./useToasts";

const REFRESH_DEBOUNCE_MS = 40;

export type LoadState = "loading" | "ready" | "failed";

export interface Workspace {
  api: TaskApi;
  /** The whole state of the list: the filter in the box and the sort in effect. */
  query: ListQuery;
  /** Every sidebar shortcut, whether or not it is current. */
  shortcuts: Shortcut[];
  /** The shortcuts the query equals right now; derived, never stored. Empty after a hand edit. */
  currentShortcuts: Shortcut[];
  /** Follows from the sort alone. */
  grouping: Grouping;
  /** Replaces the whole query with the shortcut's preset. */
  selectShortcut(shortcut: Shortcut): void;
  /** Sets the filter text, keeping the sort in effect. */
  changeFilter(filter: string): void;
  changeSort(sort: Sort): void;
  tasks: TaskView[];
  loadState: LoadState;
  queryProblem: string | undefined;
  projects: ProjectTree;
  /** Tags used by open tasks, with counts. */
  tags: TagCount[];
  /** Open tasks that belong to no project. */
  inbox: number;
  savedFilters: SavedFilter[];
  selectedId: TaskId | undefined;
  select(id: TaskId | undefined): void;
  syncStatus: SyncStatus;
  toasts: Toasts;
  actions: WorkspaceActions;
}

export interface WorkspaceActions {
  addFromText(text: string): Promise<boolean>;
  complete(task: TaskView): Promise<void>;
  reopen(task: TaskView): Promise<void>;
  remove(task: TaskView, scope: SeriesScope): Promise<void>;
  restore(task: TaskView): Promise<void>;
  edit(id: TaskId, patch: TaskPatch, scope?: SeriesScope): Promise<boolean>;
  run(command: Command, message: string): Promise<boolean>;
  saveFilter(name: string, query: ListQuery): Promise<boolean>;
  undo(): Promise<void>;
  redo(): Promise<void>;
  syncNow(): Promise<void>;
}

const EMPTY_PROJECTS: ProjectTree = { roots: [] };

interface LoadedTasks {
  queryKey: string;
  views: TaskView[];
}

const NO_TASKS: TaskView[] = [];
const NO_QUICK_ADD_DEFAULTS: Shortcut["quickAddDefaults"] = {};

export function useWorkspace(api: TaskApi, initialQuery: ListQuery = DEFAULT_QUERY): Workspace {
  const [query, setQuery] = useState<ListQuery>(initialQuery);
  const [loaded, setLoaded] = useState<LoadedTasks>({ queryKey: "", views: [] });
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const newestRequest = useRef(0);
  const [queryProblem, setQueryProblem] = useState<string | undefined>(undefined);
  const [projects, setProjects] = useState<ProjectTree>(EMPTY_PROJECTS);
  const [openTasks, setOpenTasks] = useState<TaskView[]>([]);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [selectedId, setSelectedId] = useState<TaskId | undefined>(undefined);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(api.syncStatus());
  const toasts = useToasts();

  const { filter, sort } = query;
  const queryKey = `${sort}\u0000${filter}`;
  const tasks = loaded.queryKey === queryKey ? loaded.views : NO_TASKS;
  const shownLoadState: LoadState = loaded.queryKey === queryKey ? loadState : "loading";
  const refresh = useCallback(async () => {
    const request = ++newestRequest.current;
    try {
      const [found, tree, filters, open] = await Promise.all([
        api.query(filter, sort).then(
          (views) => ({ views, problem: undefined }),
          (failure: unknown) => ({ views: [] as TaskView[], problem: toApiError(failure).message }),
        ),
        api.projects(),
        api.savedFilters(),
        api.query("", "Urgency"),
      ]);
      if (request !== newestRequest.current) return;
      setLoaded({ queryKey, views: found.views });
      setQueryProblem(found.problem);
      setProjects(tree);
      setOpenTasks(open);
      setSavedFilters(filters);
      setLoadState("ready");
    } catch {
      if (request === newestRequest.current) setLoadState("failed");
    }
  }, [api, filter, queryKey, sort]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- every state update in refresh happens after an await
    void refresh();
  }, [refresh]);

  useEffect(() => {
    api.sync().catch(() => undefined);
  }, [api]);

  useEffect(() => {
    let pending: number | undefined;
    const unsubscribe = api.subscribe((event) => {
      if (event.kind === "sync-status") {
        setSyncStatus(event.status);
        return;
      }
      window.clearTimeout(pending);
      pending = window.setTimeout(() => void refresh(), REFRESH_DEBOUNCE_MS);
    });
    return () => {
      window.clearTimeout(pending);
      unsubscribe();
    };
  }, [api, refresh]);

  const showFailure = toasts.show;
  const guarded = useCallback(
    async <T,>(work: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await work();
      } catch (failure) {
        const message = failure instanceof QuickAddError ? failure.message : toApiError(failure).message;
        showFailure(message, { tone: "error" });
        return undefined;
      }
    },
    [showFailure],
  );

  const undo = useCallback(async () => {
    const result = await guarded(() => api.undo());
    if (result !== undefined) reportReversal("Undid the last action", "Nothing could be undone", result, showFailure);
    await refresh();
  }, [api, guarded, refresh, showFailure]);

  const redo = useCallback(async () => {
    const result = await guarded(() => api.redo());
    if (result !== undefined) reportReversal("Redid the action", "Nothing could be redone", result, showFailure);
    await refresh();
  }, [api, guarded, refresh, showFailure]);

  const run = useCallback(
    async (command: Command, message: string): Promise<boolean> => {
      const result = await guarded(() => api.apply(command));
      if (result === undefined) return false;
      showFailure(message, { action: { label: "Undo", run: () => void undo() } });
      await refresh();
      return true;
    },
    [api, guarded, refresh, showFailure, undo],
  );

  const tags = useMemo(() => tagCounts(openTasks), [openTasks]);
  const inbox = useMemo(() => inboxCount(openTasks), [openTasks]);
  const shortcuts = useMemo<Shortcut[]>(
    () => [
      ...BUILTIN_SHORTCUTS.map(({ id }) => builtinShortcut(id)),
      ...savedFilters.map(savedFilterShortcut),
      ...tags.map(({ tag }) => tagShortcut(tag)),
      ...allProjectPaths(projects).map(projectShortcut),
    ],
    [projects, savedFilters, tags],
  );
  const matchingShortcuts = useMemo(() => currentShortcuts(query, shortcuts), [query, shortcuts]);
  const quickAddDefaults = matchingShortcuts[0]?.quickAddDefaults ?? NO_QUICK_ADD_DEFAULTS;

  const actions = useMemo<WorkspaceActions>(
    () => ({
      run,
      undo,
      redo,
      async addFromText(text) {
        const parsed = parseQuickAdd(text);
        if (parsed.problems.length > 0) {
          showFailure(parsed.problems.join(" "), { tone: "error" });
          return false;
        }
        const command = await guarded(() =>
          commandForQuickAdd(api, parsed.draft, quickAddDefaults),
        );
        if (command === undefined) return false;
        const project = parsed.draft.project ?? quickAddDefaults.project ?? "";
        const where = project.trim() === "" ? " in Inbox" : ` in ${project}`;
        return run(command, `Added "${parsed.draft.title}"${where}`);
      },
      async complete(task) {
        await run({ Complete: task.task.id }, `Completed "${task.task.title}"`);
      },
      async reopen(task) {
        await run({ Uncomplete: task.task.id }, `Reopened "${task.task.title}"`);
      },
      async remove(task, scope) {
        await run({ Delete: { id: task.task.id, scope } }, `Deleted "${task.task.title}"`);
      },
      async restore(task) {
        await run({ Restore: task.task.id }, `Restored "${task.task.title}"`);
      },
      edit: (id, patch, scope = "ThisInstance") => run(editCommand(id, patch, scope), "Saved"),
      saveFilter: (name, saved) =>
        run({ SaveFilter: { name, expr: saved.filter, sort: saved.sort } }, `Saved filter "${name}"`),
      async syncNow() {
        await guarded(() => api.sync());
        await refresh();
      },
    }),
    [api, guarded, quickAddDefaults, redo, refresh, run, showFailure, undo],
  );

  const selectShortcut = useCallback((shortcut: Shortcut) => {
    setQuery(shortcut.query);
    setSelectedId(undefined);
  }, []);

  const changeFilter = useCallback((next: string) => {
    setQuery((current) => ({ ...current, filter: next }));
    setSelectedId(undefined);
  }, []);

  const changeSort = useCallback((next: Sort) => {
    setQuery((current) => ({ ...current, sort: next }));
  }, []);

  return {
    api,
    query,
    shortcuts,
    currentShortcuts: matchingShortcuts,
    selectShortcut,
    changeFilter,
    grouping: groupingForSort(sort),
    changeSort,
    tasks,
    loadState: shownLoadState,
    queryProblem,
    projects,
    tags,
    inbox,
    savedFilters,
    selectedId,
    select: setSelectedId,
    syncStatus,
    toasts,
    actions,
  };
}

function reportReversal(
  doneMessage: string,
  nothingMessage: string,
  result: ApplyResult,
  show: Toasts["show"],
): void {
  const details = skippedMessages(result);
  if (result.applied.length === 0) {
    show(nothingMessage, { tone: "error", details });
    return;
  }
  show(details.length > 0 ? `${doneMessage}, but some steps were skipped` : doneMessage, { details });
}
